import React, { useState, useEffect, useRef } from 'react';
import {
  DollarSign,
  Target,
  CheckCircle,
  TrendingUp,
  Award,
  Clock,
  Users,
  FileText,
  Zap,
  Search
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const ScholarshipsDemo = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [animationStage, setAnimationStage] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [matchProgress, setMatchProgress] = useState(0);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView && !hasPlayedOnce) {
          setIsInView(true);
          setHasPlayedOnce(true);
          startAnimations();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView, hasPlayedOnce]);

  const startAnimations = () => {
    // Reset states for replay
    setAnimationStage(0);
    setMatchProgress(0);
    setAnimationsComplete(false);

    // Progressive animation stages
    setTimeout(() => setAnimationStage(1), 500);
    setTimeout(() => setAnimationStage(2), 1500);
    setTimeout(() => setAnimationStage(3), 2500);

    // Match progress animation
    setTimeout(() => {
      const interval = setInterval(() => {
        setMatchProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            // Mark animations complete and call callback
            setTimeout(() => {
              setAnimationsComplete(true);
              onAnimationComplete && onAnimationComplete();
            }, 1000);
            return 100;
          }
          return prev + 5;
        });
      }, 50);
    }, 1000);
  };

  const scholarships = [
    {
      name: "NSF Research Experience",
      amount: "$5,500",
      deadline: "Feb 15",
      match: 94,
      reason: "Perfect match: Engineering focus + research experience + academic record",
      category: "STEM Research",
      color: "gold",
      requirements: ["3.5+ GPA", "STEM major", "Research interest"],
      timeToApply: "45 min"
    },
    {
      name: "First-Gen Engineer Scholarship",
      amount: "$8,000",
      deadline: "Mar 1",
      match: 91,
      reason: "High match: First-generation status + engineering major + leadership",
      category: "Identity-Based",
      color: "orange",
      requirements: ["First-gen college", "Engineering", "Essay"],
      timeToApply: "2 hours"
    },
    {
      name: "Community Impact Award",
      amount: "$3,000",
      deadline: "Apr 15",
      match: 87,
      reason: "Strong match: Community service + leadership roles",
      category: "Leadership",
      color: "coral",
      requirements: ["Community service", "Leadership role", "Recommendations"],
      timeToApply: "90 min"
    }
  ];

  const getMatchColor = (match) => {
    if (match >= 90) return 'text-emerald-600 bg-emerald-100';
    if (match >= 80) return 'text-azure-600 bg-azure-100';
    return 'text-yellow-600 bg-yellow-100';
  };


  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-6 lg:mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 rounded-full">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              Scholarship Intelligence
            </span>
          </div>
        </div>
        <h2 className="text-2xl lg:text-4xl font-bold text-slate-900 mb-2 lg:mb-4">
          Smart Scholarship Matching
        </h2>
        <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
        Find scholarships you can actually win, sorted by deadline and how well you match – so can you focus on opportunities that make sense
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Profile Analysis */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Match Analysis</h3>
            </div>

            <div className="space-y-4">
              <div className="text-center">
                <div className={`text-3xl font-bold mb-1 transition-all duration-1000 ${
                  animationStage >= 1 ? 'text-slate-900' : 'text-slate-300'
                }`}>
                  {animationStage >= 1 ? '47' : '0'}
                </div>
                <div className="text-sm text-slate-600">Potential Matches</div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">High Match (90%+)</span>
                  <span className="text-sm font-medium text-emerald-600">8</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Good Match (80-89%)</span>
                  <span className="text-sm font-medium text-azure-600">15</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Worth Applying (70-79%)</span>
                  <span className="text-sm font-medium text-indigo-400">24</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-emerald-400">
                <div className="text-sm font-medium text-emerald-800 mb-1">
                  Total Potential Value
                </div>
                <div className={`text-xl font-bold transition-all duration-1000 ${
                  animationStage >= 2 ? 'text-emerald-900' : 'text-emerald-300'
                }`}>
                  {animationStage >= 2 ? '$127,500' : '$0'}
                </div>
                <div className="text-xs text-emerald-600">
                  Based on your top 15 matches
                </div>
              </div>
            </div>
          </div>

          {/* Competitive Advantage */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Your Advantages</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-900">First-Gen Status</div>
                  <div className="text-xs text-slate-600">Opens 23 exclusive opportunities</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-900">STEM Focus</div>
                  <div className="text-xs text-slate-600">Engineering scholarships less competitive</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Geographic Diversity</div>
                  <div className="text-xs text-slate-600">Rural background valued by top schools</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Top Matches */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-teal-900">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-stone-100">Top Scholarship Matches</h3>
                  <p className="text-sm text-stone-100">Prioritized by fit and deadline</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-2000"
                      style={{ width: `${matchProgress}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-emerald-300">
                    {matchProgress}%
                  </span>
                </div>
              </div>
            </div>

            {/* Scholarship Cards */}
            <div className="p-6 space-y-4">
              {scholarships.map((scholarship, index) => (
                <div
                  key={scholarship.name}
                  className={`border border-slate-200 rounded-lg p-4 transition-all duration-700 ${
                    animationStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="mb-1">
                        <h4 className="font-semibold text-slate-900">{scholarship.name}</h4>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {scholarship.amount}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due {scholarship.deadline}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {scholarship.timeToApply}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`px-2 py-1 text-xs font-bold rounded ${getMatchColor(scholarship.match)}`}>
                        {scholarship.match}% MATCH
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-slate-700 mb-3">
                    {scholarship.reason}
                  </div>

                  <div className="flex justify-end">
                    <button className="px-3 py-1 bg-teal-900 text-white text-sm rounded hover:bg-emerald-700 transition-colors">
                      Apply Now
                    </button>
                  </div>
                </div>
              ))}

              {/* View More */}
              <div className="text-center pt-4">
                <button className="text-emerald-600 text-sm font-medium hover:text-emerald-700 transition-colors flex items-center gap-1 mx-auto">
                  <Search className="w-4 h-4" />
                  View all 47 matches
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 text-center">
              <div className={`text-2xl font-bold transition-all duration-1000 ${
                animationStage >= 2 ? 'text-slate-900' : 'text-slate-300'
              }`}>
                {animationStage >= 2 ? '12' : '0'}
              </div>
              <div className="text-sm text-slate-600">This Month</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 text-center">
              <div className={`text-2xl font-bold transition-all duration-1000 ${
                animationStage >= 2 ? 'text-slate-900' : 'text-slate-300'
              }`}>
                {animationStage >= 2 ? '23' : '0'}
              </div>
              <div className="text-sm text-slate-600">Next 3 Months</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 text-center">
              <div className={`text-2xl font-bold transition-all duration-1000 ${
                animationStage >= 2 ? 'text-slate-900' : 'text-slate-300'
              }`}>
                {animationStage >= 2 ? '47' : '0'}
              </div>
              <div className="text-sm text-slate-600">Total Matches</div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Controls */}
      <SectionControls
        onReplay={startAnimations}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />
    </div>
  );
};

export default ScholarshipsDemo;